import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  Shield, 
  MapPin, 
  Clock, 
  CheckCircle2,
  Sparkles,
  Phone,
  Calendar,
  Award,
  Heart,
  Truck
} from 'lucide-react';
import { DeliveryBookingDialog } from './DeliveryIntegration';

/**
 * ServiceCardByCategory - Renders category-specific service cards
 * Based on user requirements for different visual treatments per category
 */

// Category-specific configurations
const CATEGORY_CONFIG = {
  'kids-clothing': {
    labelPrefix: '🧒 ',
    trustEmphasis: true,
    showParentReviews: true,
    ctaText: 'Book service',
    themeColor: 'blue',
    trustBadgeText: '🛡️ Parent-trusted provider',
  },
  'laundry-dryclean': {
    labelPrefix: '🧺 ',
    showPickupDelivery: true,
    showExpressOption: true,
    ctaText: 'Book service',
    themeColor: 'cyan',
    trustBadgeText: '✔ Quality assured',
  },
  'shoes-footwear': {
    labelPrefix: '👟 ',
    visualHeavy: true,
    showTurnaround: true,
    ctaText: 'Book now',
    themeColor: 'gray',
  },
  'bags-accessories': {
    labelPrefix: '👜 ',
    luxuryStyle: true,
    showInsurance: true,
    ctaText: 'Get a quote',
    themeColor: 'amber',
    trustBadgeText: '🛡️ Insured · Authenticated',
  },
  'jewellery-watches': {
    labelPrefix: '💍 ',
    certificationRequired: true,
    showInsuranceCoverage: true,
    ctaText: 'Get a quote',
    themeColor: 'yellow',
    trustBadgeText: '🛡️ Insured · 🎓 Certified',
  },
  'beauty-skincare': {
    labelPrefix: '💄 ',
    showAvailability: true,
    mobileVisitOption: true,
    ctaText: 'Book appointment',
    themeColor: 'pink',
    trustBadgeText: '✔ Hygiene verified',
  },
  'perfumes-cosmetics': {
    labelPrefix: '🌸 ',
    premiumStyle: true,
    onlineOption: true,
    ctaText: 'Book consultation',
    themeColor: 'purple',
  },
  'traditional-wear': {
    labelPrefix: '🧵 ',
    artisanStyle: true,
    showCraftsmanship: true,
    ctaText: 'Get a quote',
    themeColor: 'orange',
    trustBadgeText: '✔ Hand embroidery',
  },
  'mens-clothing': {
    labelPrefix: '👔 ',
    precisionStyle: true,
    ctaText: 'Book service',
    themeColor: 'slate',
  },
  'womens-clothing': {
    labelPrefix: '👗 ',
    ctaText: 'Book service',
    themeColor: 'purple',
  },
};

const THEME_COLORS = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', accent: 'text-gray-600', button: 'bg-gray-800 hover:bg-gray-900' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600', button: 'bg-amber-600 hover:bg-amber-700' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', accent: 'text-yellow-600', button: 'bg-yellow-600 hover:bg-yellow-700' },
  pink: { bg: 'bg-pink-50', border: 'border-pink-200', accent: 'text-pink-600', button: 'bg-pink-600 hover:bg-pink-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-600', button: 'bg-orange-600 hover:bg-orange-700' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600', button: 'bg-slate-700 hover:bg-slate-800' },
  cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', accent: 'text-cyan-600', button: 'bg-cyan-600 hover:bg-cyan-700' },
};

// Main component
const ServiceCardByCategory = ({
  provider,
  category = 'womens-clothing',
  onBook,
  onQuote,
  isSponsored = false,
}) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG['womens-clothing'];
  const theme = THEME_COLORS[config.themeColor] || THEME_COLORS.purple;

  const handleAction = () => {
    if (config.ctaText.toLowerCase().includes('quote')) {
      onQuote?.(provider);
    } else {
      onBook?.(provider);
    }
  };

  // Kids Clothing Card - Trust-heavy
  if (category === 'kids-clothing') {
    return (
      <Card className={`overflow-hidden border-2 ${theme.border} ${theme.bg}`} data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2 text-xs">
            {isSponsored && (
              <Badge variant="outline" className="bg-blue-100 text-blue-700 text-xs">
                🔹 Sponsored · Kids&apos; Service
              </Badge>
            )}
          </div>

          {/* Service Name */}
          <h3 className="font-bold text-lg flex items-center gap-2">
            <span className="text-xl">🧒</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          {/* Trust Badges - Emphasized for kids */}
          <div className="mt-3 space-y-1">
            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-100 px-2 py-1 rounded">
              <Shield className="h-4 w-4" />
              Parent-trusted provider
            </div>
            {provider.verification_level >= 2 && (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                Verified tailor
              </div>
            )}
          </div>

          {/* Rating with parent emphasis */}
          <div className="flex items-center justify-between mt-3 text-sm">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium">{provider.rating?.toFixed(1)}</span>
              <span className="text-gray-500">({provider.review_count} parents)</span>
            </div>
            {provider.distance && (
              <span className="text-gray-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {provider.distance}
              </span>
            )}
          </div>

          {/* Price */}
          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          {/* CTA */}
          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            {config.ctaText}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Shoes/Footwear Card - Visual, high-intent
  if (category === 'shoes-footwear') {
    return (
      <Card className="overflow-hidden" data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge variant="outline" className="text-xs mb-2">
              🔹 Sponsored · Service
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>👟</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          {/* Quick features */}
          <div className="mt-3 space-y-1">
            {provider.achievements?.includes('quick_delivery') && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                Same-day cleaning
              </p>
            )}
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Premium materials
            </p>
          </div>

          {/* Rating & Distance */}
          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              {provider.rating?.toFixed(1)} ({provider.review_count})
            </span>
            {provider.distance && (
              <span className="text-gray-500">· {provider.distance}</span>
            )}
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Book now
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Bags/Accessories Card - Luxury focused
  if (category === 'bags-accessories') {
    return (
      <Card className={`overflow-hidden border-2 ${theme.border}`} data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-amber-100 text-amber-700 text-xs mb-2">
              🔹 Sponsored · Luxury Specialist
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>👜</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          {/* Trust badges for luxury */}
          <div className="mt-3 space-y-1">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <Shield className="h-4 w-4" />
              Insured · Authenticated
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              📸 Photo quote in 10 mins
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 text-sm">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider.rating?.toFixed(1)}</span>
            <span className="text-gray-500">({provider.review_count})</span>
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Get a quote
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Jewellery/Watches Card - Certification required
  if (category === 'jewellery-watches') {
    return (
      <Card className={`overflow-hidden border-2 ${theme.border} ${theme.bg}`} data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-yellow-100 text-yellow-700 text-xs mb-2">
              🔹 Sponsored · Certified
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>💍</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          {/* Mandatory trust indicators */}
          <div className="mt-3 p-2 bg-yellow-100 rounded-lg space-y-1">
            <div className="flex items-center gap-2 text-sm text-yellow-800 font-medium">
              <Shield className="h-4 w-4" />
              Insured up to £25,000
            </div>
            <div className="flex items-center gap-2 text-sm text-yellow-800 font-medium">
              <Award className="h-4 w-4" />
              Certified jeweller
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              {provider.rating?.toFixed(1)} ({provider.review_count})
            </span>
            {provider.years_in_business && (
              <span className="text-gray-500">· {provider.years_in_business} yrs experience</span>
            )}
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Get a quote
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Beauty/Skincare Card - Booking focused
  if (category === 'beauty-skincare') {
    return (
      <Card className="overflow-hidden" data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-pink-100 text-pink-700 text-xs mb-2">
              🔹 Sponsored · Beauty Service
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>💄</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          <div className="mt-3 space-y-1">
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Hygiene verified
            </p>
            <p className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Mobile visits available
            </p>
          </div>

          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              {provider.rating?.toFixed(1)} ({provider.review_count})
            </span>
            <span className="text-green-600 font-medium flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Today available
            </span>
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Book appointment
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Traditional Wear Card - Artisan style
  if (category === 'traditional-wear') {
    return (
      <Card className={`overflow-hidden border-2 ${theme.border}`} data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-orange-100 text-orange-700 text-xs mb-2">
              🔹 Sponsored · Artisan
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>🧵</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          <div className="mt-3 space-y-1">
            <p className="text-sm text-orange-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Hand embroidery
            </p>
            <p className="text-sm text-orange-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Event-based fitting
            </p>
          </div>

          <div className="flex items-center gap-2 mt-3 text-sm">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider.rating?.toFixed(1)}</span>
            <span className="text-gray-500">({provider.review_count})</span>
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Get a quote
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Perfumes/Cosmetics Card - Premium advisory
  if (category === 'perfumes-cosmetics') {
    return (
      <Card className="overflow-hidden" data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-purple-100 text-purple-700 text-xs mb-2">
              🔹 Sponsored · Premium
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>🌸</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Personal scent profile
          </p>

          <div className="mt-3 space-y-1">
            <p className="text-sm text-purple-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              1-to-1 session
            </p>
            <p className="text-sm text-purple-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Online available
            </p>
          </div>

          <div className="flex items-center gap-2 mt-3 text-sm">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider.rating?.toFixed(1)}</span>
            <span className="text-gray-500">({provider.review_count})</span>
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
            Book consultation
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Laundry & Drycleaners Card - Service focused
  if (category === 'laundry-dryclean') {
    return (
      <Card className={`overflow-hidden border-2 ${theme.border} ${theme.bg}`} data-testid={`service-card-${category}`}>
        <CardContent className="p-4">
          {isSponsored && (
            <Badge className="bg-cyan-100 text-cyan-700 text-xs mb-2">
              🔹 Sponsored · Laundry Service
            </Badge>
          )}

          <h3 className="font-bold text-lg flex items-center gap-2">
            <span>🧺</span>
            {provider.service_name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {provider.specializations?.slice(0, 3).join(' · ')}
          </p>

          {/* Service features */}
          <div className="mt-3 space-y-1">
            <p className="text-sm text-cyan-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Free pick-up & delivery
            </p>
            {provider.achievements?.includes('quick_delivery') && (
              <p className="text-sm text-cyan-600 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Express: Same-day available
              </p>
            )}
            <p className="text-sm text-cyan-600 flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              Specialty cleaning available
            </p>
          </div>

          {/* Rating & Distance */}
          <div className="flex items-center gap-3 mt-3 text-sm">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              {provider.rating?.toFixed(1)} ({provider.review_count})
            </span>
            {provider.distance && (
              <span className="text-gray-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {provider.distance}
              </span>
            )}
          </div>

          <p className="text-sm mt-2">
            From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
          </p>

          <div className="flex gap-2 mt-4">
            <Button className={`flex-1 ${theme.button}`} onClick={() => onBook?.(provider)}>
              Book service
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => onQuote?.(provider)}>
              Get quote
            </Button>
          </div>
          
          {/* Delivery Integration */}
          <div className="mt-3 pt-3 border-t border-cyan-200">
            <DeliveryBookingDialog 
              laundryProvider={provider}
              trigger={
                <Button variant="outline" className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                  <Truck className="h-4 w-4 mr-2" />
                  Schedule Pickup & Delivery
                </Button>
              }
              onSuccess={(order) => {
                console.log('Delivery order created:', order);
                alert(`Pickup scheduled! Order ID: ${order.id}`);
              }}
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Default card (Women's/Men's Clothing)
  return (
    <Card className="overflow-hidden" data-testid={`service-card-${category}`}>
      <CardContent className="p-4">
        {isSponsored && (
          <Badge variant="outline" className="text-xs mb-2">
            <Sparkles className="h-3 w-3 mr-1" />
            Sponsored
          </Badge>
        )}

        <h3 className="font-bold text-lg">{provider.service_name}</h3>
        <p className="text-sm text-gray-600 mt-1">
          {provider.specializations?.slice(0, 3).join(' · ')}
        </p>

        {/* Verification badges */}
        {provider.verification_level >= 2 && (
          <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            Verified provider
          </div>
        )}

        {/* Rating & Location */}
        <div className="flex items-center gap-3 mt-3 text-sm">
          <span className="flex items-center gap-1">
            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
            {provider.rating?.toFixed(1)} ({provider.review_count})
          </span>
          {provider.distance && (
            <span className="text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {provider.distance}
            </span>
          )}
        </div>

        <p className="text-sm mt-2">
          From <span className="font-bold text-lg">{provider.currency} {provider.starting_price}</span>
        </p>

        <Button className={`w-full mt-4 ${theme.button}`} onClick={handleAction}>
          {config.ctaText}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ServiceCardByCategory;
